import { useState, useEffect } from 'react'
import { X, Download, Share, Plus } from 'lucide-react'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

const PROMPT_DISMISSED_AT_KEY = 'pwa-prompt-dismissed-at'
const DISMISS_DURATION_MS = 14 * 24 * 60 * 60 * 1000

function isStandaloneApp() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  )
}

function shouldShowInstallPrompt() {
  const userAgent = navigator.userAgent
  const isMobileUserAgent = /Android|iPad|iPhone|iPod/i.test(userAgent)
  const isSmallScreen = window.matchMedia('(max-width: 768px)').matches
  const isTouchFirst = window.matchMedia('(hover: none) and (pointer: coarse)').matches

  return isMobileUserAgent || isSmallScreen || isTouchFirst
}

function wasRecentlyDismissed() {
  const dismissedAt = Number(localStorage.getItem(PROMPT_DISMISSED_AT_KEY))

  return Number.isFinite(dismissedAt) && Date.now() - dismissedAt < DISMISS_DURATION_MS
}

export function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [showIOSPrompt, setShowIOSPrompt] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (wasRecentlyDismissed()) {
      setDismissed(true)
      return
    }

    if (isStandaloneApp() || !shouldShowInstallPrompt()) {
      return
    }

    // Listen for beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)

    // Check for iOS Safari
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as unknown as { MSStream?: unknown }).MSStream
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent)

    let timer: ReturnType<typeof setTimeout> | null = null
    if (isIOS && isSafari) {
      // Show iOS-specific prompt after a delay
      timer = setTimeout(() => {
        setShowIOSPrompt(true)
      }, 3000)
    }

    // Cleanup: always remove listener and clear timer if set
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      if (timer) clearTimeout(timer)
    }
  }, [])

  const handleInstall = async () => {
    if (!deferredPrompt) return

    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice

    if (outcome === 'accepted') {
      setDeferredPrompt(null)
    }
  }

  const handleDismiss = () => {
    setDismissed(true)
    setDeferredPrompt(null)
    setShowIOSPrompt(false)
    localStorage.setItem(PROMPT_DISMISSED_AT_KEY, String(Date.now()))
  }

  // Don't show if already dismissed or no prompt available
  if (dismissed || (!deferredPrompt && !showIOSPrompt)) {
    return null
  }

  // iOS Safari prompt
  if (showIOSPrompt) {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 safe-area-bottom">
        <div className="mx-auto max-w-md rounded-xl border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">A</span>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Install AsiliFlow</h3>
                <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
                  Add to your home screen for the best experience
                </p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="mt-3 rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
            <p className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
              <span className="inline-flex items-center justify-center w-5 h-5 bg-primary-100 rounded text-primary-600">
                <Share className="w-3 h-3" />
              </span>
              Tap the Share button in Safari
            </p>
            <p className="mt-2 flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
              <span className="inline-flex items-center justify-center w-5 h-5 bg-primary-100 rounded text-primary-600">
                <Plus className="w-3 h-3" />
              </span>
              Then tap "Add to Home Screen"
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Standard install prompt (Chrome, Edge, etc.)
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 safe-area-bottom">
      <div className="mx-auto max-w-md rounded-xl border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-900">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">A</span>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Install AsiliFlow</h3>
              <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
                Get quick access from your home screen
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-3 flex gap-2">
          <button
            onClick={handleDismiss}
            className="flex-1 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Not now
          </button>
          <button
            onClick={handleInstall}
            className="flex-1 px-3 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 flex items-center justify-center gap-1.5"
          >
            <Download className="w-4 h-4" />
            Install
          </button>
        </div>
      </div>
    </div>
  )
}
